import React from 'react';
import { cn } from './lib/utils';
import { Colors } from './types';

interface TypographyProps {
  variant?:
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'subtitle1'
    | 'subtitle2'
    | 'body1'
    | 'body2'
    | 'caption'
    | 'overline'
    | 'button'
    | 'p';
  style?: string;
  className?: string;
  children: React.ReactNode;
  color?: 'default' | Colors | 'white';
}

interface Props {
  className?: string;
  children: React.ReactNode;
}

const getStyleClasses = (style?: string) => {
  if (!style || typeof style !== 'string') return '';

  const styles = style.split('-');

  return styles
    .map((s) => {
      switch (s) {
        case 'blockquote':
          return 'border-l-2 pl-6 italic';
        case 'muted':
          return 'opacity-50';
        case 'italic':
          return 'italic';
        case 'underline':
          return 'underline';
        case 'light':
          return 'font-light';
        case 'bold':
          return 'font-semibold';
        case 'xbold':
          return 'font-black';
        default:
          return '';
      }
    })
    .join(' ');
};

const H1: React.FC<TypographyProps> = ({
  style,
  color = 'default',
  className,
  children,
}) => (
  <h1
    className={cn(
      'scroll-m-20 text-7xl leading-[1.235] font-medium',
      getStyleClasses(style),
      color === 'default' ? 'text-foreground' : `text-${color}`,
      className
    )}
  >
    {children}
  </h1>
);

const H2: React.FC<TypographyProps> = ({
  style,
  color = 'default',
  className,
  children,
}) => (
  <h2
    className={cn(
      'scroll-m-20 text-5xl leading-[1.235] font-medium',
      getStyleClasses(style),
      color === 'default' ? 'text-foreground' : `text-${color}`,
      className
    )}
  >
    {children}
  </h2>
);

const H3: React.FC<TypographyProps> = ({
  style,
  color = 'default',
  className,
  children,
}) => (
  <h3
    className={cn(
      'scroll-m-20 text-[2.55rem] leading-[1.235] font-medium',
      getStyleClasses(style),
      color === 'default' ? 'text-foreground' : `text-${color}`,
      className
    )}
  >
    {children}
  </h3>
);

const H4: React.FC<TypographyProps> = ({
  style,
  color = 'default',
  className,
  children,
}) => (
  <h4
    className={cn(
      'scroll-m-20 text-3xl leading-[1.235] font-medium',
      getStyleClasses(style),
      color === 'default' ? 'text-foreground' : `text-${color}`,
      className
    )}
  >
    {children}
  </h4>
);

const H5: React.FC<TypographyProps> = ({
  style,
  color = 'default',
  className,
  children,
}) => (
  <h5
    className={cn(
      'scroll-m-20 text-[1.29rem] leading-[1.235] font-medium',
      getStyleClasses(style),
      color === 'default' ? 'text-foreground' : `text-${color}`,
      className
    )}
  >
    {children}
  </h5>
);

const H6: React.FC<TypographyProps> = ({
  style,
  color = 'default',
  className,
  children,
}) => (
  <h6
    className={cn(
      'scroll-m-20 text-xl leading-[1.235] font-medium',
      getStyleClasses(style),
      color === 'default' ? 'text-foreground' : `text-${color}`,
      className
    )}
  >
    {children}
  </h6>
);

const Text: React.FC<Props> = ({ className, children }) => {
  return <div className={cn('text-sm', className)}>{children}</div>;
};

const P: React.FC<TypographyProps> = ({
  style,
  color = 'default',
  className,
  children,
}) => (
  <p
    className={cn(
      'text-sm [&:not(:first-child)]:mt-6',
      getStyleClasses(style),
      color === 'default' ? 'text-foreground' : `text-${color}`,
      className
    )}
  >
    {children}
  </p>
);

const Blockquote: React.FC<TypographyProps> = ({
  style,
  color = 'default',
  className,
  children,
}) => (
  <blockquote
    className={cn(
      'mt-6 border-l-2 pl-6 italic',
      getStyleClasses(style),
      color === 'default' ? 'text-foreground' : `text-${color}`,
      className
    )}
  >
    {children}
  </blockquote>
);

const Muted: React.FC<TypographyProps> = ({
  style,
  color = 'default',
  className,
  children,
}) => (
  <p
    className={cn(
      'text-sm opacity-50',
      getStyleClasses(style),
      color === 'default' ? 'text-foreground' : `text-${color}`,
      className
    )}
  >
    {children}
  </p>
);

const Subtitle1: React.FC<TypographyProps> = ({
  style,
  color = 'default',
  className,
  children,
}) => (
  <h6
    className={cn(
      'text-base font-medium leading-6',
      getStyleClasses(style),
      color === 'default' ? 'text-foreground' : `text-${color}`,
      className
    )}
  >
    {children}
  </h6>
);

const Subtitle2: React.FC<TypographyProps> = ({
  style,
  color = 'default',
  className,
  children,
}) => (
  <h6
    className={cn(
      'text-sm font-medium leading-5',
      getStyleClasses(style),
      color === 'default' ? 'text-foreground' : `text-${color}`,
      className
    )}
  >
    {children}
  </h6>
);

const Body1: React.FC<TypographyProps> = ({
  style,
  color = 'default',
  className,
  children,
}) => (
  <p
    className={cn(
      'text-base leading-normal',
      getStyleClasses(style),
      color === 'default' ? 'text-foreground' : `text-${color}`,
      className
    )}
  >
    {children}
  </p>
);

const Body2: React.FC<TypographyProps> = ({
  style,
  color = 'default',
  className,
  children,
}) => (
  <p
    className={cn(
      'text-sm font-sans leading-normal',
      getStyleClasses(style),
      color === 'default' ? 'text-foreground' : `text-${color}`,
      className
    )}
  >
    {children}
  </p>
);

const Caption: React.FC<TypographyProps> = ({
  style,
  color = 'default',
  className,
  children,
}) => (
  <span
    className={cn(
      'text-xs leading-5',
      getStyleClasses(style),
      color === 'default' ? 'text-foreground' : `text-${color}`,
      className
    )}
  >
    {children}
  </span>
);

const Overline: React.FC<TypographyProps> = ({
  style,
  color = 'default',
  className,
  children,
}) => (
  <span
    className={cn(
      'text-xs font-light uppercase leading-5 tracking-wider',
      getStyleClasses(style),
      color === 'default' ? 'text-foreground' : `text-${color}`,
      className
    )}
  >
    {children}
  </span>
);

const Button: React.FC<TypographyProps> = ({
  style,
  color = 'default',
  className,
  children,
}) => (
  <span
    className={cn(
      'text-sm font-medium leading-5 uppercase',
      getStyleClasses(style),
      color === 'default' ? 'text-foreground' : `text-${color}`,
      className
    )}
  >
    {children}
  </span>
);

const Typography: React.FC<TypographyProps> = ({
  variant = 'p',
  style,
  color = 'default',
  className,
  children,
}) => {
  const variants = {
    h1: (
      <H1 style={style} color={color} className={className}>
        {children}
      </H1>
    ),
    h2: (
      <H2 style={style} color={color} className={className}>
        {children}
      </H2>
    ),
    h3: (
      <H3 style={style} color={color} className={className}>
        {children}
      </H3>
    ),
    h4: (
      <H4 style={style} color={color} className={className}>
        {children}
      </H4>
    ),
    h5: (
      <H5 style={style} color={color} className={className}>
        {children}
      </H5>
    ),
    h6: (
      <H6 style={style} color={color} className={className}>
        {children}
      </H6>
    ),
    p: (
      <P style={style} color={color} className={className}>
        {children}
      </P>
    ),
    blockquote: (
      <Blockquote style={style} color={color} className={className}>
        {children}
      </Blockquote>
    ),
    muted: (
      <Muted style={style} color={color} className={className}>
        {children}
      </Muted>
    ),
    subtitle1: (
      <Subtitle1 style={style} color={color} className={className}>
        {children}
      </Subtitle1>
    ),
    subtitle2: (
      <Subtitle2 style={style} color={color} className={className}>
        {children}
      </Subtitle2>
    ),
    body1: (
      <Body1 style={style} color={color} className={className}>
        {children}
      </Body1>
    ),
    body2: (
      <Body2 style={style} color={color} className={className}>
        {children}
      </Body2>
    ),
    caption: (
      <Caption style={style} color={color} className={className}>
        {children}
      </Caption>
    ),
    overline: (
      <Overline style={style} color={color} className={className}>
        {children}
      </Overline>
    ),
    button: (
      <Button style={style} color={color} className={className}>
        {children}
      </Button>
    ),
  };

  return variants[variant];
};

export {
  Typography,
  H1,
  H2,
  H3,
  H4,
  H5,
  H6,
  P,
  Blockquote,
  Muted,
  Subtitle1,
  Subtitle2,
  Body1,
  Body2,
  Caption,
  Overline,
  Button,
  Text,
};
